import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  WithMeta,
  WithData,
  Nullable,
  Ranking,
  WeeklyRankingEntry,
  RankingPeriod,
  RankingSnapshot,
  RankingSystem,
  User,
  UserWithToken,
} from '@ygoargentina/sdk/api/api.types';
import { USER_WITH_COSSY_AND_PICTURE } from './fragments';
import { Format } from './formats';

export { RankingSystem };

export type DecklistCardEntry = {
  konamiId: number;
  quantity: number;
  card?: {
    konamiId: number;
    name?: string | null;
    image?: string | null;
  } | null;
};

export type ParsedDecklist = {
  main: DecklistCardEntry[];
  extra: DecklistCardEntry[];
  side: DecklistCardEntry[];
};

export type HallOfFamePlayer = Pick<
  User,
  | 'id'
  | 'name'
  | 'secondName'
  | 'lastName'
  | 'secondLastName'
  | 'cossyId'
  | 'picture'
> & {
  // Nullable a propósito: el schema dice `deck: String` y el API devuelve null
  // cuando el cupo no tiene mazo cargado en el torneo. Tiparlo como `string`
  // hacía que un `.toLowerCase()` pasara el check-types y explotara en runtime.
  deck?: string | null;
  decklist?: string | null;
  userId?: number | null;
  position?: number;
  parsedDecklist?: ParsedDecklist | null;
  user?: Pick<
    User,
    | 'id'
    | 'name'
    | 'secondName'
    | 'lastName'
    | 'secondLastName'
    | 'picture'
    | 'cossyId'
  > | null;
};

export type HallOfFameEntry = {
  id: number;
  disabled?: boolean;
  name: string;
  winner: HallOfFamePlayer;
  topPlayers: Array<HallOfFamePlayer & { position: number }>;
  date: string;
  tournamentId?: number | null;
  // El torneo vinculado; si es un top-cut, `swissId` apunta al torneo Swiss
  // principal (el "torneo entero").
  tournament?: { id: number; swissId?: number | null } | null;
};

export enum HallType {
  YCS = 'YCS',
  YACS = 'YACS',
  Regional = 'Regional',
  National = 'National',
  OTS = 'OTS',
}

export type HallOfFame = Record<HallType, HallOfFameEntry[]>;

export const NATIONAL_RANKING_SYSTEM = RankingSystem.HYBRID;
export const LOCAL_RANKING_SYSTEM = RankingSystem.LEAGUE;
export const PROVINCE_RANKING_SYSTEM = RankingSystem.LEAGUE;

/**
 * El sistema del ranking nacional de un formato: HYBRID, salvo el general de
 * Time Wizard, que tiene el suyo (topes por formato y bonus por formatos).
 * Los rankings por tienda, provincia y región son LEAGUE en todos.
 */
export const nationalRankingSystemFor = (formatId: number) =>
  formatId === Format.TimeWizard
    ? RankingSystem.TIME_WIZARD
    : NATIONAL_RANKING_SYSTEM;

export const getRanking = async (filters: Record<string, unknown> = {}) =>
  getGraphQLQueryClient(null).request<WithMeta<Ranking>>(
    gql`
      ${USER_WITH_COSSY_AND_PICTURE}
      query ranking($filters: RankingFilters) {
        data: ranking(filters: $filters) {
          data {
            points
            totalTournaments
            averageTournamentSize
            reportedPoints
            unreportedPoints
            reportedTournaments
            unreportedTournaments
            metadata

            user {
              ...UserWithCossyAndPicture
            }
          }
          meta {
            total
          }
        }
      }
    `,
    {
      filters,
    }
  );

export const getWeeklyRanking = async (filters: Record<string, unknown> = {}) =>
  getGraphQLQueryClient(null).request<{
    data: {
      data: WeeklyRankingEntry[];
      meta: { total: number };
      currentWeekDate: string;
      previousWeekDate: string;
    };
  }>(
    gql`
      ${USER_WITH_COSSY_AND_PICTURE}
      query weeklyRanking($filters: WeeklyRankingFilters) {
        data: weeklyRanking(filters: $filters) {
          data {
            currentPosition
            previousPosition
            positionChange
            currentPoints
            previousPoints
            pointsChange
            totalTournaments
            averageTournamentSize
            reportedPoints
            unreportedPoints
            reportedTournaments
            unreportedTournaments
            isNew
            metadata

            user {
              ...UserWithCossyAndPicture
            }
          }
          meta {
            total
          }
          currentWeekDate
          previousWeekDate
        }
      }
    `,
    {
      filters,
    }
  );

// Ranking Periods

export type RankingPeriodFilters = {
  formatId?: number;
  organizerId?: number;
  countryId?: number;
  isActive?: boolean;
  isFrozen?: boolean;
  page?: number;
  limit?: number;
};

export type CreateRankingPeriodInput = {
  name: string;
  slug: string;
  dateFrom: Date;
  dateTo: Date;
  formatId?: number;
  organizerId?: number;
  system?: string;
};

export type UpdateRankingPeriodInput = {
  name?: string;
  isActive?: boolean;
};

export const getRankingPeriods = async (filters: RankingPeriodFilters = {}) =>
  getGraphQLQueryClient(null).request<WithMeta<RankingPeriod>>(
    gql`
      query rankingPeriods($filters: RankingPeriodFilters) {
        data: rankingPeriods(filters: $filters) {
          data {
            id
            name
            slug
            dateFrom
            dateTo
            formatId
            organizerId
            system
            isActive
            isFrozen
            createdAt
            updatedAt
            format {
              id
              name
              slug
            }
            organizer {
              id
              name
            }
          }
          meta {
            total
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getRankingPeriod = async (slug: string) =>
  getGraphQLQueryClient(null).request<WithData<RankingPeriod>>(
    gql`
      query rankingPeriod($slug: String!) {
        data: rankingPeriod(slug: $slug) {
          id
          name
          slug
          dateFrom
          dateTo
          formatId
          organizerId
          system
          isActive
          isFrozen
          createdAt
          updatedAt
          format {
            id
            name
            slug
          }
          organizer {
            id
            name
          }
        }
      }
    `,
    { slug }
  );

export const getRankingPeriodSnapshots = async (
  slug: string,
  formatId: number,
  organizerId?: number | null,
  page?: number,
  limit?: number,
  countryId?: number | null,
  stateId?: number | null
) =>
  getGraphQLQueryClient(null).request<{
    data: {
      data: RankingSnapshot[];
      meta: { total: number; lastPage: number };
      period: RankingPeriod;
    };
  }>(
    gql`
      ${USER_WITH_COSSY_AND_PICTURE}
      query rankingPeriodSnapshots(
        $slug: String!
        $formatId: Int!
        $organizerId: Int
        $stateId: Int
        $countryId: Int
        $page: Int
        $limit: Int
      ) {
        data: rankingPeriodSnapshots(
          slug: $slug
          formatId: $formatId
          organizerId: $organizerId
          stateId: $stateId
          countryId: $countryId
          page: $page
          limit: $limit
        ) {
          data {
            id
            userId
            periodId
            stateId
            position
            points
            totalTournaments
            averageTournamentSize
            reportedPoints
            unreportedPoints
            reportedTournaments
            unreportedTournaments
            metadata
            user {
              ...UserWithCossyAndPicture
            }
          }
          meta {
            total
            lastPage
          }
          period {
            id
            name
            slug
            dateFrom
            dateTo
            system
          }
        }
      }
    `,
    { slug, formatId, organizerId, stateId, page, limit, countryId }
  );

export const createRankingPeriod = async (
  user: Nullable<UserWithToken>,
  input: CreateRankingPeriodInput
) =>
  getGraphQLQueryClient(user).request<WithData<RankingPeriod>>(
    gql`
      mutation createRankingPeriod($input: CreateRankingPeriodInput!) {
        data: createRankingPeriod(input: $input) {
          id
          name
          slug
        }
      }
    `,
    { input }
  );

export const updateRankingPeriod = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: UpdateRankingPeriodInput
) =>
  getGraphQLQueryClient(user).request<WithData<RankingPeriod>>(
    gql`
      mutation updateRankingPeriod(
        $id: Int!
        $input: UpdateRankingPeriodInput!
      ) {
        data: updateRankingPeriod(id: $id, input: $input) {
          id
          name
          isActive
        }
      }
    `,
    { id, input }
  );

export const freezeRankingPeriod = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<RankingPeriod>>(
    gql`
      mutation freezeRankingPeriod($id: Int!) {
        data: freezeRankingPeriod(id: $id) {
          id
          name
          slug
          isFrozen
        }
      }
    `,
    { id }
  );

export const deleteRankingPeriod = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<RankingPeriod>>(
    gql`
      mutation deleteRankingPeriod($id: Int!) {
        data: deleteRankingPeriod(id: $id) {
          id
        }
      }
    `,
    { id }
  );
